import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ToastContext, type ToastRequest } from '@/components/toast-context';

interface ToastState extends ToastRequest {
  id: number;
}

/**
 * Удаление и выполнение обратимы в течение сессии: у каждого действия есть
 * «Отменить» (docs/PRODUCT.md, раздел 5). Сообщение объявляется через
 * live region, иначе для screen reader его не существует.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((next: ToastRequest) => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ ...next, id: Date.now() });
    timer.current = setTimeout(() => setToast(null), 7000);
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-6"
      >
        {toast && (
          <div
            key={toast.id}
            className="pointer-events-auto flex max-w-md items-center gap-4 rounded-card border border-line bg-surface-2 px-4 py-3 text-sm shadow-lg"
          >
            <span className="text-ink">{toast.message}</span>
            {toast.actionLabel && (
              <Button
                size="sm"
                variant="ghost"
                className="text-accent"
                onClick={() => {
                  toast.onAction?.();
                  setToast(null);
                }}
              >
                {toast.actionLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
